// The painted parts of the five map marker looks (R1291,
// ui-map-interface.md §17.3.66).
//
// The segment and circle batches draw no filled polygon, no even-odd cut and
// no gradient, and a marker needs all three: the pin, the flag, the disc
// with its glyph and the flare column. The atlas therefore bakes each of
// them one time as a cell, and the quad carries the place and the fade. The
// marker menu of §8 paints its targets with the same code, so a target in
// the menu and a marker on the map are one picture.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed
// and imports no engine and no DOM. Every figure is the `map_markers` table
// of `chart.toml`; an alpha here is the card's own ink weight, and the
// names say which part of the card it paints.

import type { CHART } from '../gen/presentation';
import {
  MARKER_AUDIENCES, MARKER_KIND_SLUGS, glyphPath, pencilLoop,
  type MarkerAudienceSlug, type MarkerKindSlug,
} from './map-markers';
import type { MapMarkerStyle } from '../ui/settings';
import { rgba } from './color';

/** The figures of the piece, as `chart.toml` states them. */
export type MarkerFig = typeof CHART.pieces.map_markers.fig;

/**
 * The four inks of round 062, as packed `0xrrggbb` numbers: the white of a
 * squad mark, the water black under a mark, the chrome accent of a faction
 * mark, and the pale pencil. The caller passes them in, because the atlas
 * and the palette own them and a leaf holds no colour of its own.
 */
export interface MarkerInks {
  white: number;
  ink: number;
  accent: number;
  pale: number;
}

/** The part of a 2D context the painters use. */
export type PaintCtx = Pick<CanvasRenderingContext2D,
  'beginPath' | 'moveTo' | 'lineTo' | 'quadraticCurveTo' | 'arc' | 'rect' | 'closePath'
  | 'fill' | 'stroke' | 'translate' | 'save' | 'restore' | 'fillText' | 'createLinearGradient'
  | 'fillRect'>
  & {
    fillStyle: string | CanvasGradient | CanvasPattern;
    strokeStyle: string | CanvasGradient | CanvasPattern;
    lineWidth: number;
    lineJoin: CanvasLineJoin;
    lineCap: CanvasLineCap;
    font: string;
    textAlign: CanvasTextAlign;
    textBaseline: CanvasTextBaseline;
  };

// ---------------------------------------------------------------------------
// The atlas keys
// ---------------------------------------------------------------------------

/** The cells of the looks. `pulse` and `head` differ only in the well. */
export type MarkerCell = 'pin' | 'pinglyph' | 'flag' | 'pulse' | 'head' | 'column';

/**
 * The atlas key of one cell: `mmark:<cell>[:<kind>]:<audience>`, and the
 * column alone as `mmark:column`. The pin body carries no kind, because
 * every kind wears one pin.
 */
export function markerCellKey(
  cell: MarkerCell, kind: MarkerKindSlug | null, aud: MarkerAudienceSlug | null,
): string {
  if (cell === 'column') return 'mmark:column';
  if (cell === 'pin') return PIN_KEYS[aud as string] ?? `mmark:pin:${aud}`;
  return CELL_KEYS[cell][kind as string]?.[aud as string] ?? `mmark:${cell}:${kind}:${aud}`;
}

// Every key of a kind and an audience the lists hold, written once, so the
// chart piece that asks for the cells of each marker on each frame builds
// no string. A part the lists do not hold builds its key as above.
const PIN_KEYS: Record<string, string> = {};
const CELL_KEYS: Record<MarkerCell, Record<string, Record<string, string>>> = {
  pin: {}, pinglyph: {}, flag: {}, pulse: {}, head: {}, column: {},
};
for (const aud of MARKER_AUDIENCES) PIN_KEYS[aud] = `mmark:pin:${aud}`;
for (const cell of ['pinglyph', 'flag', 'pulse', 'head'] as const) {
  for (const kind of MARKER_KIND_SLUGS) {
    const row: Record<string, string> = (CELL_KEYS[cell][kind] = {});
    for (const aud of MARKER_AUDIENCES) row[aud] = `mmark:${cell}:${kind}:${aud}`;
  }
}

/** The parts of a key, or null when the key is not a marker cell. */
export function readMarkerCellKey(key: string):
{ cell: MarkerCell; kind: MarkerKindSlug | null; aud: MarkerAudienceSlug | null } | null {
  const p = key.split(':');
  if (p[0] !== 'mmark') return null;
  if (p.length === 2) return p[1] === 'column' ? { cell: 'column', kind: null, aud: null } : null;
  const aud = audienceOf(p[p.length - 1]);
  if (aud === null) return null;
  if (p.length === 3) return p[1] === 'pin' ? { cell: 'pin', kind: null, aud } : null;
  const kind = kindOf(p[2]);
  if (p.length !== 4 || kind === null) return null;
  const cell = p[1];
  if (cell !== 'pinglyph' && cell !== 'flag' && cell !== 'pulse' && cell !== 'head') return null;
  return { cell, kind, aud };
}

/** The audience a key part names, or null. */
function audienceOf(v: string): MarkerAudienceSlug | null {
  return (MARKER_AUDIENCES as readonly string[]).includes(v) ? v as MarkerAudienceSlug : null;
}

/** The kind a key part names, or null. */
function kindOf(v: string): MarkerKindSlug | null {
  return (MARKER_KIND_SLUGS as readonly string[]).includes(v) ? v as MarkerKindSlug : null;
}

// ---------------------------------------------------------------------------
// The card inks
// ---------------------------------------------------------------------------

/** The ink weights of the cards, named for what they paint. */
const W = {
  /** A squad pin, flag disc: the white body. */
  body: 0.95,
  /** The dot on the cell under a pin. */
  dot: 0.9,
  /** The water black glyph cut into a white squad body. */
  cut: 0.9,
  /** The water black glyph cut into the flag. */
  flagCut: 0.95,
  /** The water black well of a faction pin. */
  pinWell: 0.55,
  /** The water black well of a faction pulse disc (card 03). */
  pulseWell: 0.5,
  /** The water black well of a faction flare head (card 10). */
  headWell: 0.55,
  /** The accent outline and glyph of a faction mark. */
  line: 0.95,
  /** The accent flag of card 02. */
  flag: 0.92,
  /** The fade of the flare column at its top, against its foot. */
  columnTop: 0.15,
} as const;

/** The pin head's arc: from 135 to 45 degrees, the long way round. */
const PIN_ARC_FROM = Math.PI * 0.75;
const PIN_ARC_TO = Math.PI * 0.25;

// ---------------------------------------------------------------------------
// The cells
// ---------------------------------------------------------------------------

/** A filled disc. */
function disc(c: PaintCtx, x: number, y: number, r: number, style: string): void {
  c.beginPath();
  c.arc(x, y, r, 0, Math.PI * 2);
  c.fillStyle = style;
  c.fill();
}

/**
 * Card 01, the pin body, origin at the pin point: the dot on the cell and
 * the teardrop. A squad pin is white; a faction pin is a water black well
 * with an accent outline.
 */
export function paintPinBody(c: PaintCtx, aud: MarkerAudienceSlug, f: MarkerFig, k: MarkerInks): void {
  disc(c, 0, 0, f.pin_dot_r, rgba(k.white, W.dot));
  c.beginPath();
  c.arc(0, -f.pin_head_dy, f.pin_head_r, PIN_ARC_FROM, PIN_ARC_TO);
  c.lineTo(0, 0);
  c.closePath();
  if (aud === 'squad') {
    c.fillStyle = rgba(k.white, W.body);
    c.fill();
    return;
  }
  c.fillStyle = rgba(k.ink, W.pinWell);
  c.fill();
  c.lineWidth = f.pin_width;
  c.lineJoin = 'round';
  c.lineCap = 'round';
  c.strokeStyle = rgba(k.accent, W.line);
  c.stroke();
}

/**
 * Card 01, the glyph in the pin head, origin at the pin point. A squad
 * glyph is cut in water black and keeps its weight over the whole life; a
 * faction glyph is an accent stroke and fades with the pin.
 */
export function paintPinGlyph(
  c: PaintCtx, kind: MarkerKindSlug, aud: MarkerAudienceSlug, f: MarkerFig, k: MarkerInks,
): void {
  c.translate(0, -f.pin_head_dy);
  if (aud === 'squad') {
    c.fillStyle = rgba(k.ink, W.cut);
    glyphPath(c, kind, f.pin_glyph);
    c.fill('evenodd');
    return;
  }
  c.lineWidth = f.pin_glyph_width;
  c.strokeStyle = rgba(k.accent, W.line);
  glyphPath(c, kind, f.pin_glyph_hollow);
  c.stroke();
}

/**
 * Card 02, the flag, origin where it meets the staff at its top edge. A
 * squad flag has a swallow tail; a faction flag is square. The glyph is cut
 * into the flag in water black.
 */
function paintFlag(
  c: PaintCtx, kind: MarkerKindSlug, aud: MarkerAudienceSlug, f: MarkerFig, k: MarkerInks,
): void {
  c.beginPath();
  c.moveTo(0, 0);
  c.lineTo(f.flag_w, 0);
  if (aud === 'squad') c.lineTo(f.flag_w - f.flag_notch, f.flag_h / 2);
  c.lineTo(f.flag_w, f.flag_h);
  c.lineTo(0, f.flag_h);
  c.closePath();
  c.fillStyle = rgba(k.accent, W.flag);
  c.fill();
  c.translate(f.flag_glyph_dx, f.flag_h / 2);
  c.fillStyle = rgba(k.ink, W.flagCut);
  glyphPath(c, kind, f.flag_glyph);
  c.fill('evenodd');
}

/**
 * Cards 03 and 10, the disc with its glyph, origin at the centre. A squad
 * disc is white with the glyph cut in water black; a faction disc is a
 * water black well, an accent ring and an accent glyph stroke. `well` is
 * the one weight the two cards draw differently.
 */
function paintDisc(
  c: PaintCtx, kind: MarkerKindSlug, aud: MarkerAudienceSlug, f: MarkerFig, k: MarkerInks,
  cell: 'pulse' | 'head',
): void {
  if (aud === 'squad') {
    disc(c, 0, 0, f.disc_r, rgba(k.white, W.body));
    c.fillStyle = rgba(k.ink, W.cut);
    glyphPath(c, kind, f.disc_glyph);
    c.fill('evenodd');
    return;
  }
  disc(c, 0, 0, f.disc_r, rgba(k.ink, cell === 'pulse' ? W.pulseWell : W.headWell));
  c.beginPath();
  c.arc(0, 0, f.disc_r, 0, Math.PI * 2);
  c.lineWidth = f.ring_width;
  c.strokeStyle = rgba(k.accent, W.line);
  c.stroke();
  c.lineWidth = f.glyph_width;
  glyphPath(c, kind, f.disc_glyph_hollow);
  c.stroke();
}

/** The height of the baked column in world px. The quad stretches it. */
const COLUMN_CELL_H = 16;
/** Its width. The quad sets the real width, so this only keeps a texel. */
const COLUMN_CELL_W = 2;

/**
 * Card 10, the column, origin at its foot: WHITE, full at the foot and
 * `columnTop` at the top. The quad carries the accent and the life alpha,
 * and stretches the cell to the column of the moment.
 */
function paintColumn(c: PaintCtx): void {
  const g = c.createLinearGradient(0, 0, 0, -COLUMN_CELL_H);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(1, `rgba(255,255,255,${W.columnTop})`);
  c.fillStyle = g;
  c.fillRect(-COLUMN_CELL_W / 2, -COLUMN_CELL_H, COLUMN_CELL_W, COLUMN_CELL_H);
}

/** Paints one atlas cell by its parts. The atlas bake calls this. */
export function paintMarkerCell(
  c: PaintCtx, cell: MarkerCell, kind: MarkerKindSlug | null, aud: MarkerAudienceSlug | null,
  f: MarkerFig, k: MarkerInks,
): void {
  const a: MarkerAudienceSlug = aud ?? 'squad';
  const g: MarkerKindSlug = kind ?? 'attack';
  switch (cell) {
    case 'pin': paintPinBody(c, a, f, k); return;
    case 'pinglyph': paintPinGlyph(c, g, a, f, k); return;
    case 'flag': paintFlag(c, g, a, f, k); return;
    case 'pulse': case 'head': paintDisc(c, g, a, f, k, cell); return;
    case 'column': paintColumn(c); return;
  }
}

// ---------------------------------------------------------------------------
// The marker menu
// ---------------------------------------------------------------------------

/** The plain glyph of a card with no `menu` of its own, in screen px. */
const MENU_GLYPH = 9;
/** The flag of card 02's menu, in screen px: left, top, right, bottom. */
const MENU_FLAG = { x0: -14, y0: -11, x1: 14, y1: 9, glyph: 7.5, dy: -1 } as const;
/** The loop of card 05's menu, and where its word stands under it. */
const MENU_LOOP = { r: 8, seed: 5, lift: -4, wordY: 14, px: 11, width: 1.2 } as const;
/** The ink weights of the menu: the plain glyph, the pencil and its word. */
const MENU_W = { glyph: 0.95, pencil: 0.9, word: 0.95 } as const;

/**
 * Paints one target of the marker menu, the origin at its centre and three
 * px over it, as round 062 `drawMenu` placed it. Card 02 paints its flag,
 * card 05 its loop and its word, and the other three picks the plain glyph
 * in white. `word` is the pencil word of the kind in the reader's language,
 * and `fell` the Fell font stack.
 */
export function paintMenuTarget(
  c: PaintCtx, look: MapMarkerStyle, kind: MarkerKindSlug, k: MarkerInks,
  word: string, fell: string,
): void {
  if (look === 'pennant') {
    c.beginPath();
    c.moveTo(MENU_FLAG.x0, MENU_FLAG.y0); c.lineTo(MENU_FLAG.x1, MENU_FLAG.y0);
    c.lineTo(MENU_FLAG.x1, MENU_FLAG.y1); c.lineTo(MENU_FLAG.x0, MENU_FLAG.y1);
    c.closePath();
    c.fillStyle = rgba(k.accent, W.flag);
    c.fill();
    c.fillStyle = rgba(k.ink, W.flagCut);
    c.translate(0, MENU_FLAG.dy);
    glyphPath(c, kind, MENU_FLAG.glyph);
    c.fill('evenodd');
    return;
  }
  if (look === 'pencil') {
    const pts = pencilLoop(MENU_LOOP.r, MENU_LOOP.seed, kind === 'danger');
    c.beginPath();
    pts.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
    // The card moved the origin after it laid the path, so the loop stays
    // and the word stands `lift` px higher than its figure says.
    c.translate(0, MENU_LOOP.lift);
    c.lineWidth = MENU_LOOP.width;
    c.lineJoin = 'round';
    c.lineCap = 'round';
    c.strokeStyle = rgba(k.pale, MENU_W.pencil);
    c.stroke();
    c.font = `italic ${MENU_LOOP.px}px ${fell}`;
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.fillStyle = rgba(k.pale, MENU_W.word);
    c.fillText(word, 0, MENU_LOOP.wordY);
    return;
  }
  c.fillStyle = rgba(k.white, MENU_W.glyph);
  glyphPath(c, kind, MENU_GLYPH);
  c.fill('evenodd');
}

/** Does a look's target carry the kind's name under it? Card 05 does not:
 *  its word is the name. */
export function menuLabelled(look: MapMarkerStyle): boolean {
  return look !== 'pencil';
}
